import { PaginatedQueryResult } from '@dispatch/shared/hooks';
import { ResultStatus } from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  DataGridPro,
  GridActionsCellItem,
  GridActionsColDef,
  GridColDef,
  GridRowParams,
  GridRowIdGetter,
  DataGridProProps,
  GridValidRowModel,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridSortModel,
  GridPaginationModel,
  GridBaseIconProps,
} from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import '../../styles/styles.css';
import { Empty } from './components/Empty';
import { Loader } from './components/Loader';
import { tableStyleHideSeparator, tableStyleOverrides } from './tableStyle';
import { MUI_X_5_GRID_PROPS } from './muiX5GridProps';
import { useGridApiRefKeepingPage } from './useGridApiRefKeepingPage';
import type { GridInitialState } from '@mui/x-data-grid-pro';

// MUI X v8 types icon slots with its own `IconProps`, whose `color: string`
// is wider than a Material icon accepts. The icon takes every prop the grid
// passes at runtime.
export const MoreActionsIcon =
  MoreHorizIcon as React.JSXElementConstructor<GridBaseIconProps>;

interface Pagination {
  pageSize: number;
  pagingOptions?: number[];
}

type Action = {
  action: any;
  label: string;
  field: string;
  icon?: React.ReactElement;
};

export type ServerSidePagingProps<T> = Omit<
  PaginatedQueryResult<T, unknown>,
  'data' | 'error' | 'status' | 'refetch' | 'pages'
> & {
  onPageSizeChange: (rowsPerPage: number) => void;
};

export interface TableProps<T extends GridValidRowModel> {
  rows: T[];
  columns: GridColDef[];
  height?: number | string;
  actions?: Action[];
  status: ResultStatus;
  emptyEntityName: string;
  getRowId?: GridRowIdGetter<T>;
  serverPaging?: ServerSidePagingProps<T>;
  pagination?: Pagination;
  /**
   * Enable built-in client-side pagination (page/rows-per-page footer) for a
   * fully-materialized `rows` set. Opt-in and default off so existing
   * consumers — which scroll unless they wire `serverPaging` — are unchanged.
   */
  clientPagination?: boolean;
  tableState?: {
    columnModel: Record<string, boolean>;
    pinnedColumns: GridPinnedColumnFields | undefined;
  };
  handleShowColumns?: (newModel: GridColumnVisibilityModel) => void;
  handlePinnedColumns?: (newPinned: GridPinnedColumnFields) => void;
  handleSort?: (sortModel: GridSortModel) => void;
  dataGridProps?: Partial<DataGridProProps>;
  tableHeight?: string;
  initialState?: GridInitialState;
  isAdjustable?: boolean;
}

/**
 * Row count for a server-paged grid. A page still loading adds no rows yet,
 * and MUI X clamps the grid back to the last loaded page, so the requested
 * page always stays in range.
 */
export function serverRowCount({
  loaded,
  hasNextPage,
  page,
  pageSize,
}: {
  loaded: number;
  hasNextPage: boolean;
  page: number;
  pageSize: number;
}): number {
  return Math.max(
    hasNextPage ? loaded + 1 : loaded,
    page > 0 ? page * pageSize + 1 : 0
  );
}

export function TableComponent<T extends GridValidRowModel>({
  rows,
  columns,
  pagination = {
    pagingOptions: [5, 10, 15, 20],
    pageSize: 10,
  },
  actions = [],
  emptyEntityName,
  status,
  getRowId,
  serverPaging,
  clientPagination = false,
  tableState,
  handleShowColumns,
  handlePinnedColumns,
  handleSort,
  tableHeight,
  isAdjustable = true,
  ...rest
}: TableProps<T>) {
  const apiRef = useGridApiRefKeepingPage();
  const [cols, setColumns] = useState(columns);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: pagination?.pageSize || 10,
  });
  useEffect(() => {
    // add actions if they exist
    if (actions.length) {
      const actionsColumn: GridActionsColDef = {
        field: 'actions',
        type: 'actions',
        width: 60,
        renderHeader: () => <MoreHorizIcon sx={{ color: 'text.secondary' }} />,
        getActions: (params: GridRowParams) => [...getActionItems(params)],
      };

      setColumns([...cols, actionsColumn]);
      return;
    }

    setColumns(columns);
  }, [actions.length, columns]);

  const getActionItems = (params: GridRowParams) => {
    return actions.map(({ action, label, field, icon }) => {
      return (
        <GridActionsCellItem
          key={label}
          data-testid="custom-element"
          icon={icon}
          label={label}
          onClick={() => action(params.row[field])}
          showInMenu
        />
      );
    });
  };

  const pageOptions = pagination?.pagingOptions
    ? [...(pagination?.pagingOptions || [])]
    : [];

  const localeText = {
    ...MUI_X_5_GRID_PROPS.localeText,
    ...rest.dataGridProps?.localeText,
  };

  function getServerSidePagingOptions(): Partial<DataGridProProps<T>> {
    if (!serverPaging) {
      return {};
    }
    const count = serverPaging?.currentTotal || 0;
    const page = serverPaging.currentPageNumber;
    return {
      localeText: {
        ...localeText,
        paginationDisplayedRows: ({ from, to }) => {
          if (serverPaging?.hasNextPage) {
            return `${from} - ${to} of more than ${to}`;
          }
          return `${from} - ${to}`;
        },
      },
      onPaginationModelChange: (model) => {
        if (model.pageSize !== paginationModel.pageSize) {
          setPaginationModel(model);
          serverPaging?.onPageSizeChange(model.pageSize);
          serverPaging?.goToFirstPage();
          return;
        }
        if (model.page > serverPaging?.currentPageNumber) {
          serverPaging?.getNextPage();
        }
        if (model.page < serverPaging?.currentPageNumber) {
          serverPaging?.getPreviousPage();
        }
      },
      paginationMode: 'server',
      paginationModel: {
        page,
        pageSize: paginationModel.pageSize,
      },
      rowCount: serverRowCount({
        loaded: count,
        hasNextPage: serverPaging.hasNextPage,
        page,
        pageSize: paginationModel.pageSize,
      }),
    };
  }

  const dataGridProps = {
    ...MUI_X_5_GRID_PROPS,
    slots: {
      moreActionsIcon: MoreActionsIcon,
      noRowsOverlay: () => (
        <Empty status={status} entityName={emptyEntityName} />
      ),
      noResultsOverlay: () => (
        <Empty status="noresult" entityName={emptyEntityName} />
      ),
      loadingOverlay: Loader,
    },
    sx: isAdjustable
      ? [tableStyleOverrides]
      : [tableStyleOverrides, tableStyleHideSeparator],
    ...rest.dataGridProps,
    localeText,
    slotProps: {
      ...MUI_X_5_GRID_PROPS.slotProps,
      ...rest.dataGridProps?.slotProps,
    },
  };
  return (
    <div style={{ height: tableHeight ?? '70vh' }} data-testid="table-card">
      <DataGridPro
        {...dataGridProps}
        apiRef={apiRef}
        disableColumnFilter={true}
        getRowId={getRowId}
        loading={status === 'loading'}
        disableVirtualization={isRunningInJest()}
        rows={[...(rows || [])]}
        columns={cols}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortingMode="server"
        onSortModelChange={handleSort}
        pageSizeOptions={pageOptions}
        disableMultipleRowSelection
        pagination={!!serverPaging || clientPagination}
        columnVisibilityModel={tableState?.columnModel}
        pinnedColumns={tableState?.pinnedColumns}
        onColumnVisibilityModelChange={handleShowColumns}
        onPinnedColumnsChange={handlePinnedColumns}
        {...getServerSidePagingOptions()}
      />
    </div>
  );
}

export default TableComponent;
